import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { UserDataProvider } from './contexts/UserDataContext';
import { WatchlistProvider } from './contexts/WatchlistContext';
import { isAuthenticated } from './utils/auth';
import routes from './utils/routes';
import authService from './services/api/auth';

// Layout components
import Layout from './components/Layout/Layout';
import AuthLayout from './components/AuthLayout/AuthLayout';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyOTP from './pages/VerifyOTP';
import Dashboard from './pages/Dashboard';

// Protected route component - modified to prevent infinite redirects
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to={routes.public.LOGIN} replace />;
  }
  return children;
};

function App() {
  // Cache authentication status to prevent multiple rechecks during rendering
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  
  // Update auth status on mount and when local storage changes
  useEffect(() => {
    // Check auth status immediately
    const currentAuthStatus = isAuthenticated();
    setIsAuth(currentAuthStatus);
    // console.log('Auth status on mount:', currentAuthStatus ? 'Authenticated' : 'Not authenticated');
    
    // Function to check authentication status
    const checkAuthStatus = () => {
      const newAuthStatus = isAuthenticated();
      if (newAuthStatus !== isAuth) {
        // console.log('Auth status changed:', newAuthStatus ? 'Authenticated' : 'Not authenticated');
        setIsAuth(newAuthStatus);
      }
    };
    
    // Set up event listeners for auth status changes
    const handleStorageChange = () => {
      checkAuthStatus();
    };
    
    // Check auth status regularly
    const authCheckInterval = setInterval(checkAuthStatus, 1000);
    
    // Set up various event listeners that could indicate auth changes
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);
    window.addEventListener('click', handleStorageChange);
    window.addEventListener('keydown', handleStorageChange);
    
    return () => {
      clearInterval(authCheckInterval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
      window.removeEventListener('click', handleStorageChange);
      window.removeEventListener('keydown', handleStorageChange);
    };
  }, [isAuth]);

  return (
    <ThemeProvider>
      <WebSocketProvider>
        <UserDataProvider autoSubscribe={isAuth}>
          <WatchlistProvider>
            <Router>
              <Routes>
                {/* Public/Auth routes - no Layout wrapper */}
                <Route 
                  path={routes.public.LOGIN} 
                  element={
                    isAuth ? 
                      <Navigate to={routes.protected.DASHBOARD} replace /> : 
                      <AuthLayout>
                        <Login />
                      </AuthLayout>
                  } 
                />
                <Route 
                  path={routes.public.REGISTER} 
                  element={
                    <AuthLayout>
                      <Register />
                    </AuthLayout>
                  } 
                />
                <Route 
                  path={routes.public.FORGOT_PASSWORD} 
                  element={
                    <AuthLayout>
                      <ForgotPassword />
                    </AuthLayout>
                  } 
                />
                <Route 
                  path={routes.public.RESET_PASSWORD} 
                  element={
                    <AuthLayout>
                      <ResetPassword />
                    </AuthLayout>
                  } 
                />
                <Route 
                  path={routes.public.VERIFY_OTP} 
                  element={
                    <AuthLayout>
                      <VerifyOTP />
                    </AuthLayout>
                  } 
                />
                
                {/* Protected routes - with Layout wrapper */}
                <Route 
                  path={routes.protected.DASHBOARD} 
                  element={
                    isAuth ? (
                      <Layout>
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      </Layout>
                    ) : (
                      <Navigate to={routes.public.LOGIN} replace />
                    )
                  } 
                />
                
                {/* Redirect to dashboard if authenticated, otherwise to login */}
                <Route 
                  path="/" 
                  element={
                    <Navigate to={isAuth ? routes.protected.DASHBOARD : routes.public.LOGIN} replace />
                  } 
                />
                
                {/* Catch all route - redirect to login */}
                <Route path="*" element={<Navigate to={routes.public.LOGIN} replace />} />
              </Routes>
            </Router>
          </WatchlistProvider>
        </UserDataProvider>
      </WebSocketProvider>
    </ThemeProvider>
  );
}

export default App;
